// src/components/appointments/Appointments.jsx - Using GenericTable
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Search, Filter, Plus, MapPin } from 'lucide-react';

// Import shadcn components
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Alert, AlertDescription } from "../ui/alert";
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Skeleton } from '../ui/skeleton';

// Import our custom components
import GenericTable from '../ui/table/GenericTable';
import PageContainer from '../ui/layout/PageContainer/PageContainer';
import StatusBadge from '../ui/feedback/StatusBadge/StatusBadge';

// Import hook
import { useEvents } from '../../hooks/useEvents';
import { useCustomers } from '../../hooks/useCustomers';

const Appointments = () => {
  const navigate = useNavigate();

  // Get data from hooks
  const {
    events: appointments,
    loading: appointmentsLoading,
    error: appointmentsError,
    deleteEvent,
    clearEventsError,
    isConnected
  } = useEvents();

  const { getCustomerById } = useCustomers();

  // Local UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Filter appointments based on search and status
  const filteredAppointments = useMemo(() => {
    if (!appointments) return [];

    return appointments.filter(appointment => {
      // Search filtering
      const matchesSearch = !searchTerm || [
        appointment.title,
        appointment.service_type,
        appointment.service_address,
        appointment.description
      ].some(field =>
        field && field.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Status filtering
      const matchesStatus = statusFilter === 'all' || appointment.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [appointments, searchTerm, statusFilter]);

  // Helper function to get customer info
  const getCustomerInfo = (customerId) => {
    if (!customerId) {
      return {
        name: 'Unknown Customer',
        email: '',
        phone: ''
      };
    }

    const customer = getCustomerById(customerId);

    if (!customer) {
      return {
        name: 'Loading...',
        email: '',
        phone: ''
      };
    }

    return {
      name: customer.name || customer.full_name || customer.customer_name,
      email: customer.email || customer.email_address,
      phone: customer.phone || customer.phone_number || customer.mobile
    };
  };

  // Helper function to get customer initials
  const getInitials = (name) => {
    if (!name) return 'UC';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Date TBD';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Helper function to format time
  const formatTime = (dateString) => {
    if (!dateString) return 'Time TBD';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Column definitions for appointments
  const columns = [
    {
      key: 'customer',
      header: 'Customer',
      render: (appointment) => {
        const customerInfo = getCustomerInfo(appointment.customer_id);
        return (
          <div className="flex items-center space-x-3">
            <Avatar>
              <AvatarFallback className="bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold">
                {getInitials(customerInfo.name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-foreground truncate">
                {customerInfo.name || 'Unknown Customer'}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                {customerInfo.email}
              </p>
            </div>
          </div>
        );
      },
      skeleton: (
        <div className="flex items-center space-x-3">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-[150px]" />
            <Skeleton className="h-3 w-[120px]" />
          </div>
        </div>
      )
    },
    {
      key: 'service',
      header: 'Service',
      render: (appointment) => {
        const serviceType = appointment.service_type || appointment.title || 'Service';
        const address = appointment.service_address;
        return (
          <div className="min-w-0">
            <p className="font-medium text-foreground truncate">
              {serviceType}
            </p>
            {address && (
              <p className="text-sm text-muted-foreground truncate flex items-center">
                <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                {address}
              </p>
            )}
          </div>
        );
      },
      skeleton: (
        <div className="space-y-2">
          <Skeleton className="h-4 w-[120px]" />
          <Skeleton className="h-3 w-[100px]" />
        </div>
      )
    },
    {
      key: 'datetime',
      header: 'Date & Time',
      render: (appointment) => (
        <div className="min-w-0">
          <p className="font-medium text-foreground">
            {formatDate(appointment.scheduled_datetime)}
          </p>
          <p className="text-sm text-muted-foreground">
            {formatTime(appointment.scheduled_datetime)}
          </p>
        </div>
      ),
      skeleton: (
        <div className="space-y-2">
          <Skeleton className="h-4 w-[100px]" />
          <Skeleton className="h-3 w-[80px]" />
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (appointment) => (
        <StatusBadge
          status={appointment.status || 'pending'}
          size="md"
        />
      ),
      skeleton: <Skeleton className="h-6 w-[80px] rounded-full" />
    }
  ];

  // Event handlers
  const handleRowClick = (appointment) => {
    navigate(`/appointments/${appointment.id}`);
  };

  const handleDelete = (appointment) => {
    deleteEvent(appointment.id);
  };

  const getDeleteConfirmMessage = (appointment) => {
    const customerInfo = getCustomerInfo(appointment.customer_id);
    const customerName = customerInfo.name || 'this customer';
    return `Are you sure you want to cancel the appointment with ${customerName}? This action cannot be undone.`;
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  // Empty state configuration
  const emptyState = {
    icon: Calendar,
    title: searchTerm || statusFilter !== 'all'
      ? 'No matching appointments'
      : 'No appointments yet',
    description: searchTerm || statusFilter !== 'all'
      ? 'Try adjusting your search or filter criteria.'
      : 'Get started by scheduling your first appointment.',
    action: (!searchTerm && statusFilter === 'all') ? {
      icon: Plus,
      label: 'Schedule First Appointment',
      onClick: () => navigate('/calendar')
    } : null
  };

  // Connection error
  if (!isConnected) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertDescription>
            Not connected to server. Please check your connection and try again.
          </AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Error Alert */}
      {appointmentsError && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>
            {appointmentsError}
            <Button
              variant="outline"
              size="sm"
              onClick={clearEventsError}
              className="ml-2"
            >
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <div className="flex space-x-4 mb-6">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search appointments..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Status Filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Generic Table */}
      <GenericTable
        data={filteredAppointments}
        columns={columns}
        loading={appointmentsLoading && appointments.length === 0}
        emptyState={emptyState}
        onRowClick={handleRowClick}
        onDelete={handleDelete}
        deleteConfirmMessage={getDeleteConfirmMessage}
        showActionsColumn={false}
      />

      {/* Results Summary */}
      {filteredAppointments.length > 0 && (
        <div className="text-center text-muted-foreground text-sm mt-6">
          Showing {filteredAppointments.length} of {appointments?.length || 0} appointments
          {appointmentsLoading && (
            <span className="ml-2">
              <span className="inline-block w-2 h-2 bg-primary rounded-full animate-pulse mr-1" />
              Updating...
            </span>
          )}
        </div>
      )}
    </PageContainer>
  );
};

export default Appointments;
